import { Form, Input, message, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { MakeApiCall } from "../../../apis";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background: #263d57;
  overflow: hidden;

  .ant-input-affix-wrapper,
  .ant-input {
    background: #fafafa !important;
    border: 1.5px solid #e8e8ea !important;
    border-radius: 10px !important;
    font-size: 13.5px !important;
    height: 46px !important;
    padding: 0 14px !important;
    color: #1d1e20 !important;
    box-shadow: none !important;
    transition: border-color 0.15s !important;
  }
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused,
  .ant-input:focus {
    border-color: #f35b68 !important;
    background: #fff !important;
  }
  .ant-input::placeholder { color: #ccc !important; }
  .ant-input-affix-wrapper .ant-input {
    height: 100% !important;
    border: none !important;
    background: transparent !important;
  }
  .ant-form-item { margin-bottom: 12px !important; }
  .ant-form-item-explain-error {
    font-size: 12px !important;
    margin-top: 4px !important;
    color: #f07070 !important;
  }
`;

const Left = styled.div`
  width: 520px;
  min-width: 420px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 52px 48px;
  box-shadow: 12px 0 48px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 2;
  animation: ${fadeUp} 0.6s ease both;

  @media (max-width: 900px) {
    width: 100%;
    min-width: unset;
    box-shadow: none;
    padding: 44px 32px;
  }
`;

const Logo = styled.div`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: #1d1e20;
  margin-bottom: 40px;
  img { height: 20px; object-fit: contain; }
`;

const Heading = styled.h1`
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.6px;
  color: #1d1e20;
  line-height: 1.25;
  margin: 0 0 6px;
`;

const SubText = styled.p`
  font-size: 13px;
  color: #535353;
  font-weight: 300;
  margin: 0 0 28px;
  line-height: 1.6;
`;

const InviteCard = styled.div`
  background: #fafafa;
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 24px;

  .invite-line {
    font-size: 13px;
    color: #535353;
    line-height: 1.65;
    margin: 0;
  }

  .invite-name,
  .invite-owner,
  .invite-role {
    font-weight: 600;
    color: #1d1e20;
  }

  .invite-email {
    display: block;
    margin-top: 8px;
    font-size: 12px;
    color: #888;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 10.5px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 5px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 13px;
  background: #f35b68;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
  transition: background 0.15s, transform 0.1s;
  letter-spacing: -0.1px;

  &:hover  { background: #d94757; }
  &:active { transform: scale(0.99); }
  &:disabled { opacity: 0.7; cursor: not-allowed; }

  .icon {
    width: 22px; height: 22px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px;
  }
`;

const BackLink = styled.div`
  text-align: center;
  font-size: 12.5px;
  color: #535353;
  margin-top: 18px;
  a { color: #f35b68; text-decoration: none; font-weight: 500; }
`;

const ErrorBox = styled.div`
  background: #fff5f5;
  border: 1px solid #ffd6d6;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #c0392b;
  line-height: 1.6;
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 0;
  color: #535353;
  font-size: 13px;
`;

const Right = styled.div`
  flex: 1;
  background: #355071;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 64px;
  position: relative;
  overflow: hidden;

  @media (max-width: 900px) { display: none; }
`;

const Glow = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(80px);
  opacity: 0.18;
  &.g1 { width: 360px; height: 360px; background: #f35b68; top: -80px; right: -80px; }
  &.g2 { width: 260px; height: 260px; background: #fff; bottom: -60px; left: -60px; opacity: 0.04; }
`;

const Ring = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  border: 0.5px solid rgba(255, 255, 255, 0.06);
  &.r1 { width: 640px; height: 640px; top: -260px; right: -220px; }
  &.r2 { width: 420px; height: 420px; top: -140px; right: -100px; border-color: rgba(243, 91, 104, 0.12); }
  &.r3 { width: 520px; height: 520px; bottom: -320px; left: -220px; }
`;

const RightInner = styled.div`
  max-width: 460px;
  width: 100%;
  position: relative;
  z-index: 1;
  text-align: center;
  animation: ${fadeUp} 0.7s ease 0.2s both;
`;

const BigIcon = styled.div`
  width: 120px;
  height: 120px;
  margin: 0 auto 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 0.5px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  color: rgba(255, 255, 255, 0.85);
  font-size: 46px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  font-weight: 600;
  color: #f9868f;
  text-transform: uppercase;
  letter-spacing: 1.6px;
  margin-bottom: 16px;
  span { width: 20px; height: 1.5px; background: #f35b68; opacity: 0.5; border-radius: 1px; display: inline-block; }
`;

const StoryHeadline = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.6px;
  line-height: 1.25;
  margin: 0 0 16px;
  em { color: #f35b68; font-style: normal; }
`;

const StoryBody = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 300;
  line-height: 1.7;
  max-width: 380px;
  margin: 0 auto 28px;
  strong { color: rgba(255, 255, 255, 0.85); font-weight: 500; }
`;

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [form] = Form.useForm();
  const [validating, setValidating] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (!token) {
      setValidationError("Invalid or missing invite link. Please ask your team admin to resend the invite.");
      setValidating(false);
      return;
    }

    let cancelled = false;

    const validateToken = async () => {
      setValidating(true);
      setValidationError("");
      try {
        const response = await MakeApiCall(
          `sub-users/invite/validate?token=${encodeURIComponent(token)}`,
          "get"
        );
        if (cancelled) return;

        if (response?.status) {
          setInviteData(response?.data || {});
        } else {
          setValidationError(
            response?.message || "This invite link is invalid or has expired."
          );
        }
      } catch {
        if (!cancelled) {
          setValidationError("Something went wrong validating your invite. Please try again.");
        }
      } finally {
        if (!cancelled) setValidating(false);
      }
    };

    validateToken();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const onFinish = async (values) => {
    if (!token) {
      message.error("Invalid or missing invite link.");
      return;
    }

    setSubmitLoading(true);
    try {
      const response = await MakeApiCall("sub-users/invite/accept", "post", {
        token,
        password: values.password,
        confirm_password: values.confirm_password,
      });

      if (response?.status === true) {
        message.destroy();
        message.success(response?.message || "Invite accepted successfully. Please sign in.");
        form.resetFields();
        navigate("/sign-in");
      } else {
        message.destroy();
        const fieldErrors = response?.data;
        const firstFieldError =
          fieldErrors && typeof fieldErrors === "object"
            ? Object.values(fieldErrors).flat().find(Boolean)
            : null;
        message.error(firstFieldError || response?.message || "Failed to accept invite. Please try again.");
      }
    } catch {
      message.destroy();
      message.error("Something went wrong. Please try again.");
    }
    setSubmitLoading(false);
  };

  const roleLabel = inviteData?.sub_user_role_label || inviteData?.sub_user_role || "team member";
  const inviteeName = inviteData?.name || "there";
  const ownerName = inviteData?.owner_name || "your team";

  return (
    <Wrapper className="h-100">
      <Left>
        <Logo>
          <img src="assets/media/SellerViewLogo.png" alt="Sellerview" />
        </Logo>

        <Heading>
          Accept your invite.
          <br />
          Join the team.
        </Heading>
        <SubText>
          Set a password to activate your SellerView account and start collaborating.
        </SubText>

        {validating ? (
          <LoadingWrap>
            <Spin />
            <span>Validating your invite…</span>
          </LoadingWrap>
        ) : validationError ? (
          <ErrorBox>{validationError}</ErrorBox>
        ) : (
          <>
            <InviteCard>
              <p className="invite-line">
                Hi <span className="invite-name">{inviteeName}</span>, you&apos;ve been invited by{" "}
                <span className="invite-owner">{ownerName}</span> as an{" "}
                <span className="invite-role">{roleLabel}</span>.
              </p>
              {inviteData?.email ? (
                <span className="invite-email">{inviteData.email}</span>
              ) : null}
            </InviteCard>

            <Form form={form} name="accept-invite" onFinish={onFinish}>
              <Label>Password</Label>
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "Please enter your password." },
                  { min: 8, message: "Password must be at least 8 characters." },
                ]}
                hasFeedback
              >
                <Input.Password placeholder="••••••••" size="large" type="password" />
              </Form.Item>

              <Label>Confirm Password</Label>
              <Form.Item
                name="confirm_password"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  { required: true, message: "Please confirm your password." },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Passwords do not match."));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="••••••••" size="large" type="password" />
              </Form.Item>

              <Form.Item style={{ marginTop: 6 }}>
                <SubmitBtn type="submit" disabled={submitLoading}>
                  {submitLoading ? (
                    "Please wait…"
                  ) : (
                    <>
                      Accept invite <div className="icon">→</div>
                    </>
                  )}
                </SubmitBtn>
              </Form.Item>
            </Form>
          </>
        )}

        <BackLink>
          <Link to="/sign-in">← Back to Sign in</Link>
        </BackLink>
      </Left>

      <Right>
        <Ring className="r1" />
        <Ring className="r2" />
        <Ring className="r3" />
        <Glow className="g1" />
        <Glow className="g2" />

        <RightInner>
          <BigIcon>👥</BigIcon>

          <Eyebrow>
            <span />
            Team access
            <span />
          </Eyebrow>

          <StoryHeadline>
            Your team&apos;s numbers, <em>shared securely.</em>
          </StoryHeadline>

          <StoryBody>
            Accept your invite to access the seller accounts and modules your admin assigned —{" "}
            <strong>all in one place.</strong>
          </StoryBody>
        </RightInner>
      </Right>
    </Wrapper>
  );
}
